import { toast } from 'sonner';
import { buildLessonDocument, documentYear } from './lessonDocument';

export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char]));

const safeLogoUrl = (url) => (/^(https?:|data:image\/)/i.test(url || '') ? url : '');

// Een Bijbelgedeelte heeft in het lesmodel alleen een verwijzing (vet) en een toelichting (gewone tekst); die toelichting
// is geen letterlijk citaat en wordt dus niet als citaat opgemaakt.
const renderBlock = (block) => {
  if (block.type === 'theme') return `<p class="theme"><strong>Thema</strong> — <em>${escapeHtml(block.text)}</em></p>`;
  if (block.type === 'subheading') return `<h3 class="sub">${escapeHtml(block.text)}</h3>`;
  if (block.type === 'list') {
    const tag = block.ordered ? 'ol' : 'ul';
    return `<${tag}>${block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</${tag}>`;
  }
  if (block.type === 'scripture') {
    return `<div class="scripture-block"><p class="scripture">${escapeHtml(block.reference)}</p>${block.text ? `<p>${escapeHtml(block.text)}</p>` : ''}</div>`;
  }
  if (block.type === 'question') return `<p class="question"><span class="qnum">${block.number}.</span> ${escapeHtml(block.text)}</p>`;
  return `<p>${escapeHtml(block.text)}</p>`;
};

// Standalone A4-document (huisstijl van de organisatie): kop CONNECTBIJEENKOMST met logo,
// genummerde onderdelen en een footer met organisatienaam. Geen vast logo of vaste naam.
export const buildLessonPrintHtml = (lesson, groupName, branding = {}) => {
  const doc = buildLessonDocument(lesson);
  const logo = safeLogoUrl(branding.logo_url);
  const year = documentYear(lesson.lesson_date);
  const sectionsHtml = doc.sections.map((section) => `
    <section>
      <h2><span class="num">${section.number}.</span> ${escapeHtml(section.label)}</h2>
      ${section.blocks.map(renderBlock).join('')}
    </section>`).join('');
  const agendaHtml = doc.agenda.length
    ? `<section><h2>Gemeenteagenda</h2>${doc.agenda.map(renderBlock).join('')}</section>` : '';
  const footer = [`CONNECTLES ${year}`, groupName, branding.name].filter(Boolean).map(escapeHtml).join(' – ');

  return `<!doctype html>
<html lang="nl">
<head>
<meta charset="utf-8">
<meta name="robots" content="noindex, nofollow">
<title>${escapeHtml(doc.title)}</title>
<style>
  @page { size: A4; margin: 12.7mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Calibri, "Segoe UI", -apple-system, Helvetica, Arial, sans-serif; color: #0E1015; font-size: 10.5pt; line-height: 1.38; }
  .header { display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 2px solid #0E1015; padding-bottom: 6px; margin-bottom: 8px; }
  h1 { font-size: 14pt; letter-spacing: 0.04em; margin: 0; }
  .header img { max-height: 48px; max-width: 120px; object-fit: contain; }
  .title { margin: 0 0 6px; font-size: 13pt; font-weight: 700; }
  h2 { font-size: 11pt; margin: 10px 0 3px; break-after: avoid; }
  .num { margin-right: 2px; }
  p { margin: 0 0 4px; white-space: pre-line; orphans: 3; widows: 3; }
  .theme { margin-bottom: 5px; }
  h3.sub { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; margin: 7px 0 2px; break-after: avoid; }
  .scripture-block { border-left: 2.5px solid #94a3b8; background: #f8fafc; padding: 2px 6px 2px 8px; margin: 0 0 4px; break-inside: avoid; }
  .scripture-block p { margin: 0; }
  .scripture { font-weight: 700; break-after: avoid; }
  ol, ul { margin: 0 0 4px; padding-left: 1.6em; }
  li { margin-bottom: 2px; break-inside: avoid; }
  .question { padding-left: 1.4em; text-indent: -1.4em; break-inside: avoid; }
  .footer { margin-top: 12px; padding-top: 4px; border-top: 1px solid #94a3b8; font-size: 8.5pt; color: #64748b; break-before: avoid; }
</style>
</head>
<body>
  <div class="header">
    <h1>CONNECTBIJEENKOMST</h1>
    ${logo ? `<img src="${escapeHtml(logo)}" alt="${escapeHtml(branding.name || '')}">` : ''}
  </div>
  <p class="title">${escapeHtml(doc.title)}</p>
  ${sectionsHtml}
  ${agendaHtml}
  <div class="footer">${footer}</div>
</body>
</html>`;
};

// Prints via a hidden same-page iframe rather than window.open(). On mobile Safari
// (especially when running as an installed PWA), window.open('', '_blank') often
// doesn't open a genuine separate tab -- it can navigate the current tab away
// instead, so closing the print sheet strands the user outside the app with no way
// back. An iframe never navigates the page at all: the print dialog appears as a
// plain OS overlay on top of the unchanged app, so dismissing it always returns
// exactly where the user was.
export const printLesson = (lesson, groupName, branding) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (error) {
      toast.error('Printen mislukt');
    }
    setTimeout(cleanup, 2000);
  };

  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(buildLessonPrintHtml(lesson, groupName, branding));
  doc.close();
};
